/**
 * DaVinci API Request Schemas
 *
 * Request schemas for DaVinci REST API endpoints.
 * Includes booking actions (BA, B, BQ, BC) and response conversion.
 */

import { z } from 'zod';

// ----- Authentication -----

/**
 * Login object (required in all requests but empty - auth handled by API Gateway)
 */
export const DaVinciLoginSchema = z.object({});

// ----- Client Information -----

/**
 * Client address information
 */
export const DaVinciAddressSchema = z.object({
  Street: z.string().nullable(),
  ZipCode: z.string().nullable(),
  City: z.string().nullable(),
  Country: z.string().nullable(),
});

/**
 * Client information
 */
export const DaVinciClientSchema = z.object({
  EMail: z.string().nullable(),
  Telephone: z.string().nullable(),
  Name: z.string(),
  Surname: z.string(),
  Address: DaVinciAddressSchema,
});

// ----- Participant Information -----

/**
 * Participant (traveler) age information.
 * All participants (adults, children, infants) carry BirthDate (.NET /Date(ms)/) on every
 * BA/B/perform-action check, so DaVinci derives the exact age at the travel date. The
 * child/infant marker is carried by the Salutation ("K"/"B"), not by the age representation.
 *
 * NOTE (RC-933, deferred): children/infants previously used AgeInYears to avoid the insurance
 * child-cap (933) miscounting them as adults. Auxiliaries/insurance are currently disabled
 * (HIDE_AUXILIARIES_STEP), so no request carries insurance and BirthDate is sent for all. When
 * insurance is re-enabled, re-test RC-933 (davinci-insurance-probe.js); if BirthDate trips 933,
 * reintroduce an AgeInYears fallback for child/infant on insurance-bearing requests. AgeInYears
 * stays in the schema for that path.
 */
export const DaVinciParticipantAgeSchema = z.object({
  BirthDate: z.string().nullable().optional(), // .NET /Date(ms)/ — all participants
  AgeInYears: z.number().optional(), // retained for the deferred RC-933 insurance fallback
});

/**
 * Participant passport information.
 *
 * Required on every Participant (adults and children) per Bewotec spec.
 * Site is German-market only; values come from DAVINCI_CONFIG.
 */
export const DaVinciPassportSchema = z.object({
  Nationality: z.string(),
  NationalityCode: z.string(),
});

/**
 * Participant (traveler) information.
 *
 * Salutation/Gender wire values per Bewotec spec (VAMOS Postman collection).
 * Adults derive both from their salutation (see mapSalutationToDaVinci):
 *   Mr       → Salutation "H", Gender 0
 *   Mrs      → Salutation "D", Gender 1
 *   Diverse  → Salutation "D", Gender 0   (interim)
 * Children always send Salutation "K" and derive Gender from their own gender
 * field (see mapGenderToDaVinci): male/diverse → 0, female → 1.
 * Both fields are required by B/BQ/BC; Gender is sent for children too.
 * Passport is required on every participant (Nationality + NationalityCode).
 */
export const DaVinciParticipantSchema = z.object({
  Salutation: z.string(),
  Gender: z.number(),
  Name: z.string(),
  DoNotAutoCreateClient: z.boolean(),
  Age: DaVinciParticipantAgeSchema.optional(),
  Address: DaVinciAddressSchema.optional(), // Address for adult travelers
  EMail: z.string().nullable().optional(), // Email for adult travelers
  Telephone: z.string().nullable().optional(), // Phone for adult travelers
  Passport: DaVinciPassportSchema,
});

// ----- Booking Service -----

/**
 * Booking service (accommodation)
 */
export const DaVinciBookingServiceSchema = z.object({
  Requirement: z.string(), // e.g., "H" for Hotel
  Service: z.string(), // Service code (e.g., "HERIRINI119")
  Accommodation: z.string(), // Room + board code (e.g., "J02H")
  Amount: z.number(),
  From: z.string(), // .NET date format: /Date(milliseconds)/
  Till: z.string(), // .NET date format
  ParticipantAssignment: z.string(), // range format, e.g., "1-3" for participants 1 through 3
});

// ----- Booking Request -----

/**
 * Booking request object (for BA action)
 */
export const DaVinciBookingRequestSchema = z.object({
  ActionCode: z.enum(['BA', 'B', 'BQ', 'BC']),
  AgencyNumber: z.number(),
  Touroperator: z.string(),
  TravelType: z.string(),
  SalesOffice: z.string(),
  BookingDate: z.string(), // .NET date format
  Services: z.array(DaVinciBookingServiceSchema),
  Client: DaVinciClientSchema,
  Participants: z.array(DaVinciParticipantSchema),
});

// ----- API Endpoints -----

/**
 * PerformAction request (BA, B, BQ, BC)
 */
export const PerformActionRequestSchema = z.object({
  Login: DaVinciLoginSchema,
  Booking: z.record(z.unknown()), // Flexible booking object
  SessionId: z.string().optional(),
  ActivityId: z.string().optional(),
  Delivery: z.record(z.unknown()).optional(),
  Payment: z.record(z.unknown()).optional(),
});

/**
 * ConvertResponseToNewRequest request
 */
export const ConvertResponseRequestSchema = z.object({
  Login: DaVinciLoginSchema,
  SessionId: z.string(),
  ActivityId: z.string(),
  Booking: z.record(z.unknown()), // Raw booking object from BA response
});

// ----- Type Exports -----

export type DaVinciLogin = z.infer<typeof DaVinciLoginSchema>;
export type DaVinciAddress = z.infer<typeof DaVinciAddressSchema>;
export type DaVinciClient = z.infer<typeof DaVinciClientSchema>;
export type DaVinciParticipantAge = z.infer<typeof DaVinciParticipantAgeSchema>;
export type DaVinciPassport = z.infer<typeof DaVinciPassportSchema>;
export type DaVinciParticipant = z.infer<typeof DaVinciParticipantSchema>;
export type DaVinciBookingService = z.infer<typeof DaVinciBookingServiceSchema>;
export type DaVinciBookingRequest = z.infer<typeof DaVinciBookingRequestSchema>;
export type PerformActionRequest = z.infer<typeof PerformActionRequestSchema>;
export type ConvertResponseRequest = z.infer<typeof ConvertResponseRequestSchema>;
